import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/modules/ui/components/command/Command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/modules/ui/components/popover/Popover';

interface ComboboxOption {
  value: string;
  label: string;
  /** Optional leading icon rendered before the label, in the list and the trigger. */
  icon?: ReactNode;
}

interface ComboboxProps {
  value?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  options: ComboboxOption[];
  disabled?: boolean;
  className?: string;
  disableSort?: boolean;
  onChange: (value: string) => void;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export function Combobox({
  value,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  options,
  disabled,
  className,
  disableSort = false,
  onChange,
  ...ariaProps
}: ComboboxProps) {
  const [open, setOpen] = useState(false);

  const sortedOptions = disableSort
    ? options
    : [...options].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true }),
      );

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          {...ariaProps}
          disabled={disabled}
          className={cn(
            "border-input data-[placeholder]:text-input-placeholder [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 dark:hover:bg-input/50 flex h-9 items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
            !selectedOption && 'text-input-placeholder',
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2 text-left">
            {selectedOption?.icon}
            <span className="truncate">
              {selectedOption?.label ?? placeholder}
            </span>
          </span>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] overflow-hidden p-0"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-[360px]">
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {sortedOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label]}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      value === option.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {option.icon}
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
